import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import { SchoolService } from "@services/school.service";
import { BehaviorSubject, interval, Observable, of, Subject } from "rxjs";
import { catchError, mergeMap, startWith, take, tap } from "rxjs/operators";
import { environment } from "@env/environment";
import { IframeService } from "./iframe.service";
import { NbDialogService } from "@nebular/theme";
import { MfaModalComponent } from "@app/account/mfa-modal/mfa-modal.component";
import { timer, Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";
import { NotificationsService } from "./notifications.service";
import { Router } from "@angular/router";

export class ScaffoldUser {
  /**The users id*/
  user_id: string;
  /** Username that is tied to the cognito authentication service*/
  cognito_username: any;
  /** The role of the user in the instance*/
  role: string;
  [key: string]: any;
}

@Injectable({
  providedIn: "root",
})
export class UserService {
  accessToken;
  refreshToken;
  accessLoading = false;
  refershToken;
  _loggedIn = false;
  mapFields = {
    given_name: "first_name",
    family_name: "last_name",
  };
  hasAttemptedCognitoAttributes: boolean;
  cognitoAttributes = new BehaviorSubject(null) 
  set loggedIn(value) {
    if (value !== this.loggedIn) {
      console.log("LOGGED IN:", value);
      this._loggedIn = value;
      this.logInState.next(value);
    }
  }
  get loggedIn() {
    return this._loggedIn;
  }
  id: string;
  logInState = new BehaviorSubject<boolean>(false);
  authSub = new Subject<any>();
  userInfo = null;
  private _roleInformation: ScaffoldUser;
  private refreshSessionTime = 50
  private _roleDetailsLoaded = false;
  private _roleDetails = new Subject<ScaffoldUser>();
  sessionDetails: any; //holds any details about the current session
  // Used to pass variables between pages
  // /login -> /new-password-required | { currentAttributes : {}, requiredAttributes : [] }
  userAttributes = {
    currentAttributes: {},
    requiredAttributes: [],
  };
  private refreshTokenTimer: Subscription;
  private authBlockTimer: Subscription;

  constructor(
    public notifications: NotificationsService,
    public dialog: NbDialogService,
    private schoolService: SchoolService,
    private cookieService: CookieService,
    private http: HttpClient,
    private iframeService: IframeService,
    private router: Router
  ) {
    this.startRefreshTokenTimer();
  }

  // call to get new access token before it expires every 50 mins, should be reset everytime access token has been set. Will not be called if no access token found. 
  public startRefreshTokenTimer(minutes?, immediateCheck?) {
    console.log("minutes", minutes);
    if (minutes) this.refreshSessionTime = minutes;
    this.refreshTokenTimer?.unsubscribe();
    const timer$ = timer(this.refreshSessionTime * 60 * 1000);
  
    this.refreshTokenTimer = (immediateCheck ? timer$.pipe(startWith(0)) : timer$)
      .pipe(
        tap(() => { console.log("REFRESH?") }),
        switchMap(() => this.accessToken ? this.refreshSession() : of())
      )
      .subscribe();
  }

  watchAuthenticationCookie(){
    this.logInState.subscribe((loggedIn) => {
      if (loggedIn) {
        this.authBlockTimer?.unsubscribe();
        this.authBlockTimer = interval(2000).subscribe(() => {
          if (!this.cookieService.get('authenticated') && !this.cookieService.get('active_session')) {
            this.router.navigate(['/login']);
          }
        });
      }else{
        this.authBlockTimer?.unsubscribe();      
      }
    }
    );
  }

  // values directly from cognito for the logged in user
  getCognitoUserAttributes$(refresh?) {
    if (!this.hasAttemptedCognitoAttributes||refresh) {
      this.hasAttemptedCognitoAttributes = true
      this.http
        .request("get", `${environment.apiRoot}/auth/userattributes`)
        .pipe(catchError((error) => of({ error })))
        .pipe(tap((response) => this.cognitoAttributes.next(this.mapUserAttributes(response))))
        .subscribe()
    }
    return this.cognitoAttributes.asObservable()
  }

  // map values from cognito to our own expected keys and  values
  mapUserAttributes(attributes) {
    return !Array.isArray(attributes?.details?.UserAttributes)
      ? {}
      : attributes?.details?.UserAttributes.reduce((acc, attribute) => {
          const key = this.mapFields[attribute.Name] || attribute.Name;
          acc[key] = attribute.Value;
          if (key.includes("_verified")) {
            acc[key] = acc[key] === "true";
          }
          return acc;
        }, {cognito_username:attributes?.details.Username});
  }

  /** Store temporary values related to the user */
  getUserAttributes() {
    if (this.sessionDetails?.userAttributes) {
      const attributes = {};
      for (let key in this.sessionDetails.userAttributes) {
        let attribute = this.sessionDetails.userAttributes[key];
        // strip imutable _verified from attributes to set
        if (!key.includes("_verified")) {
          attributes[key] = attribute;
        }
      }
      this.userAttributes.currentAttributes = attributes;
      this.userAttributes.requiredAttributes =
        this.sessionDetails.requiredAttributes;
    }
    return this.userAttributes;
  }

  /** @description Check if user is logged in
   * Will refresh access token if it is expired or missing
   */
  isLoggedIn(accessToken?, refreshToken?): Observable<boolean> {
    let observer = new Subject<boolean>();
    setTimeout(() => {
      let logged;
      logged = this.cookieService.get("authenticated") === "true" && this.cookieService.get("active_session") === "true";
      if (logged) {
        this.loggedIn = true;
        //populate role details if they are not cached
  
        if (!this.accessToken) {
          this.getAuthenitcation()
            .pipe(take(1))
            .subscribe((at) => {
              observer.next(true);
              observer.complete();
            });
        } else {
          observer.next(true);
          observer.complete();
        }
      } else {
        this.accessToken = null;
        // authenticated cookie is missing, check for refresh
        // attemptRefresh
        this.refreshSession().subscribe((ref) => {
          console.log("REFRESH:", ref);
          if (!ref) {
            observer.next(false);
            observer.complete();
          } else {
            if (!this.accessToken) {
              this.getAuthenitcation()
                .pipe(take(1))
                .subscribe((at) => {
                  observer.next(true);
                  observer.complete();
                });
            } else {
              observer.next(true);
              observer.complete();
            }
          }
        });
      }
    }, 100);
    return observer.asObservable();
  }

  /**
   *@description Sets cookies needed to determine origin of request,
   * Extra safety net to set cognito values if they go missing between login refreshes
   * */
  setCookies() {
    const poolId = this.cookieService.get("cog_user_pool_id");
    const clientId = this.cookieService.get("cog_client_id");
    const schoolName = this.cookieService.get("school_name");
    const schoolDomain = this.cookieService.get("school_domain");
    if (!poolId) {
      this.setCookie("cog_user_pool_id", environment.cognitoUserPoolId);
    }
    if (!clientId) {
      this.setCookie("cog_client_id", environment.cognitoClientId);
    }
    if (!schoolName) {
      this.setCookie("school_name", environment.name);
    }
    if (!schoolDomain) {
      this.setCookie("school_domain", environment.domain);
    }
  }
  /**
   * @description Sets a cookie, used for readability of code
   * @param cookie
   * @param value
   */
  setCookie(cookie, value) {
    this.cookieService.set(
      cookie,
      value,
      null,
      "/",
      environment.cookieDomain,
      true
    );
  }

  /**@description Refreshes the session if the access token or authenticated cookie is expired or missing
   * */
  refreshSession(): Observable<any> {
    this.startRefreshTokenTimer();
    let observer = new Subject<any>();
    this.http
      .post(environment.apiRoot + "/auth/refresh", {
        accessToken: this.accessToken,
        refreshToken: this.refreshToken,
      })
      .subscribe(
        (data: any) => {
          if (data.status === "refreshed") {
            console.log("UserService : Refresh success");
            // set a cookie to see if the session has ended since the last login attemps. this is to allow for the session details to be in an httponly cookie
            this.cookieService.set('active_session', 'true', null, '/');

            //populate role details if they are not cached
            this.getRoleDetails().subscribe((role) => {});
            this.setUserInfo(data.details.userInfo);
            if (data.access) {
              this.accessToken = data.access;
            }
            this.loggedIn = true;
            observer.next(true);
            observer.complete();
          } else {
            console.log("UserService : Refresh failed");
            this.loggedIn = false;
            observer.next(false);
            observer.complete();
          }
        },
        (err) => {
          console.log("UserService : Refresh failed", err);
          this.loggedIn = false;
          observer.next(false);
          observer.complete();
        }
      );
    return observer.asObservable();
  }

  /**
   * @description Log in a user
   * @param username
   * @param password
   */
  login(username: string, password: string): Observable<any> {
    let url = "/auth/logininit"
    if(this.router.url.includes('direct-login'))url+='?check_for_sso=true'
    if (!this.iframeService.isInIframe) {
      console.log("DELETE COOKIE");
      this.cookieService.delete("t4ntn033dd");
    }
    this.setCookies();
    let login = new Subject<any>();
    this.http
      .post(environment.apiRoot + url, { username, password })
      .subscribe((data: any) => {
        if (data.challengeName) {
          return login.next(data);
        }
        if (data.userAttributes) {
          data.userAttributes.userName = username;
          this.sessionDetails = data.userAttributes;
        }
        if (data?.details?.userInfo) {
          Object.keys(data?.details?.userInfo?.data || {}).forEach((key) => {
            if (this.mapFields[key]) {
              data.details.userInfo.data[this.mapFields[key]] =
                this.mapFields[key];
              delete data.details.userInfo.data[key];
            }
          });
          this.setUserInfo(data.details.userInfo);
        }
        // ensure that logouts are not directed to cogntio domain
        this.cookieService.set("authenticated_sso","false", null, "/");
        // set a cookie to see if the sessionhas ended since the last login attemps. this is to allow for the session details to be in an httponly cookie
        this.cookieService.set('active_session', 'true', null, '/');
        if (data.access) {
          this.startRefreshTokenTimer();
          this.accessToken = data.access;
        }
        if (data.status === "logged in") {
          this.loggedIn = true;
        }
        login.next(data);
        login.complete();
      });
    return login.asObservable();
  }

  submitChallengeSelection(username, selection, session): Observable<any> {
    return this.http
      .post(environment.apiRoot + "/auth/mfachallengeselection", {
        username,
        selection,
        session,
      })
      .pipe(catchError((error) => of({ error })))
      .pipe(
        tap((response) => {
          if (!response || response.error) {
            this.notifications.popNotification(
              "",
              "Unknown error while making MFA selection"
            );
          }
        })
      );
  }

  handleChallenge(username, usercode, session,challengeType): Observable<any> {
    return this.http
      .post(environment.apiRoot + `/auth/${challengeType}challenge`, {
        username,
        usercode,
        session,
      })
      .pipe(catchError((error) => of({ error })))
      .pipe(
        tap((response) => {
          if (response.access) {
            this.startRefreshTokenTimer();
            this.accessToken = response.access;}
          if (response?.userInfo) {
            Object.keys(response.userInfo.data).forEach((key) => {
              if (this.mapFields[key]) {
                response.userInfo.data[this.mapFields[key]] =
                  this.mapFields[key];
                delete response.userInfo.data[key];
              }
            });
            this.setUserInfo(response.userInfo);
          }
          if (response.userAttributes) {
            this.sessionDetails = response.userAttributes;
          }
        })
      );
  }

  /**
   * Watch the login state of the current user
   * */
  getLogInState(): Observable<any> {
    return this.logInState.asObservable();
  }


  /** Log out the current user and clear all user data */
  logOut() {
    // Clear everything
    const logoutSub = new Subject();
    this.http
      .post(environment.apiRoot + "/auth/logout", {})
      .subscribe((data: any) => {
        this.userInfo = null;
        this.accessToken = null;
        this.sessionDetails = null;
        this.cookieService.delete("user_info");
        this.cookieService.delete("authenticated");
        this.cookieService.delete("active_session");
        this.setCookie(
          "cog_user_pool_id",
          environment.cognitoUserPoolId
        );
        this.setCookie(
          "cog_client_id",
          environment.cognitoClientId
        );
        this.setCookie(
          "school_name",
          environment.name
        );
        this.setCookie(
          "school_domain",
          environment.domain
        );
        logoutSub.next("loggedout");
        this.loggedIn = false;
      });
    return logoutSub.asObservable();
  }

  /** Gett the password ploicy for the current instance */
  getPasswordPolicy() {
    // Circular dependency on HttpUtilService, use HttpClient directly
    return this.http.get(environment.apiRoot + "/password-policy");
  }

  /**
   * Manually change the current user password
   */
  changePassword(oldPassword, newPassword): Observable<any> {
    return this.isLoggedIn().pipe(
      mergeMap((loggedIn) => {
        return Observable.create((observer) => {
          if (loggedIn) {
            this.http
              .post(environment.apiRoot + "/auth/resetpassword", {
                oldPassword,
                newPassword,
              })
              .subscribe(
                (result) => {
                  console.log(result);
                  // reset session?
                  observer.next(result);
                  observer.complete();
                },
                (err) => {
                  observer.error(err);
                  observer.complete();
                }
              );
          } else {
            observer.error();
            observer.complete();
          }
        });
      })
    );
  }
  /**
   * Trigger forgot password event
   * @param username User to update password
   */
  triggerForgotPassword(username): Observable<any> {
    let params: {username: string, url?: string} = { username };
    return Observable.create((observer) => {
      const currentSubdomain = window.location.hostname.split('.')[0];
      if (currentSubdomain !== environment.domain) {
        params.url = window.location.hostname === 'localhost' ? 'localhost:4200' : window.location.hostname;
      }
      console.log("Forgot PW Triggered", params);
      this.http
        .post(environment.apiRoot + "/auth/forgotpassword",params)
        .subscribe(
          (data: any) => {
            console.log("Forgot PW Triggered");
            observer.next(data);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  /** Used for unauthed users (forgot password flow)*/
  confirmPassword(verificationCode, newPassword, username) {
    return Observable.create((observer) => {
      this.http
        .post(environment.apiRoot + "/auth/verifypassword", {
          verificationCode,
          newPassword,
          username,
        })
        .subscribe(
          () => {
            // We did it, new password is set
            observer.next();
            observer.complete();
          },
          (err) => {
            // Something bad happened
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  /** Authed users using a temp pw on their first login*/
  completeNewPasswordChallenge(newPassword, oldPassword, attributes) {
    return Observable.create((observer) => {
      this.http
        .post(environment.apiRoot + "/auth/completenewpassword", {
          attributes,
          newPassword,
          oldPassword,
          userName: this.sessionDetails.userName,
        })
        .subscribe(
          (data: any) => {
            if (data.status === "password reset") {
              this.getRoleDetails().subscribe((role) => {});
              if (data.userAttributes) {
                this.sessionDetails = data.userAttributes;
              }
              if (data?.details?.userInfo) {
                this.setUserInfo(data.details.userInfo);
              }
              if (data.access) {
                this.startRefreshTokenTimer();
                this.accessToken = data.access;
              }
              observer.next("Successful Login");
              observer.complete();
            } else {
              observer.error(data.error);
              observer.complete();
            }
          },
          (err) => {
            // Something bad happened
            observer.error(err);
            observer.complete();
          }
        );
    });
  }
  /** Get details about the logged in user */
  getUserInfo() {
    if (!this.userInfo) {
      const user = this.cookieService.get("user_info");
      if (user?.length > 0) {
        this.setUserInfo(JSON.parse(user));
      }
    }
    return this.userInfo;
  }
  /** Clear current user information */
  resetUserInfo() {
    return Observable.create((observer) => {
      this.userInfo = null;
      this.http
        .post(environment.apiRoot + "/auth/userattributes", {})
        .subscribe((data: any) => {
          const attributes = {};
          data.details?.UserAttributes?.forEach((attribute) => {
            // _verified is coming back as string "true | false " convert to a boolean
            if (attribute.Name.includes("_verified")) {
              attributes[attribute.Name] = attribute.Value == "true";
            } else {
              attributes[attribute.Name] = attribute.Value;
            }
          });
          if (Object.keys(attributes).length > 0) {
            attributes["username"] = data.details?.Username;
            this.setUserInfo(attributes);
          }
          observer.next(this.userInfo);
          observer.complete();
        });
    });
  }
  /**
   * Set the details about the logged in user
   * @param userInfo Details to set
   */
  setUserInfo(userInfo) {
    if (userInfo) {
      this.userInfo = userInfo;
      this.cookieService.set(
        "user_info",
        JSON.stringify(this.userInfo),
        null,
        "/",
        environment.cookieDomain,
        environment.secureCookie
      );
    }
  }
  /**
   * Get verification code for contat method
   * @param contactType Type to verify
   */
  getVerificationCode(contactType: "email" | "phone_number"): Observable<any> {
    return this.http.post(environment.apiRoot + "/auth/sendverification", {
      contactType,
    });
  }
  /**
   * Attempt to verify submitted code
   * @param contactType Type of contact
   * @param code Verification code
   */
  sendVerficationCode(
    contactType: "email" | "phone_number",
    code
  ): Observable<any> {
    return this.http.post(environment.apiRoot + "/auth/submitverfication", {
      contactType,
      code,
    });
  }

  /**
   * Get deatils about the role and permissions of the current user
   * @param refresh Force refreshing the details from the API instead of using the localy stored data
   */
  getRoleDetails(refresh?: boolean): Observable<ScaffoldUser> {
    if (refresh) {
      this._roleDetailsLoaded = false;
      this._roleInformation = null;
    }
    if (this._roleDetailsLoaded) {
      if (this._roleInformation) {
        // if cached
        return of(this._roleInformation);
      } else {
        // if not cached
        return this._roleDetails.asObservable();
      }
    } else {
      // get role detials from api
      this.http
        .get(environment.apiRoot + "/users/self?role_details=true")
        .subscribe(
          (data: ScaffoldUser) => {
            // role details have been checked
            this._roleDetailsLoaded = true;
            this._roleInformation = data;
            this._roleDetails.next(data);
          },
          (err) => {
            console.log(err);
          }
        );
      //  }
      //    });
      return this._roleDetails.asObservable();
    }
  }

  getUserInstances(inst): Observable<any> {
    const result = new Subject<any>();
    const promises = [];
    let instances = [];
    inst.forEach((instance) => {
      const promise = new Promise((resolve, reject) => {
        // Temporarily set the sub_domain cookie to the instance's sub_domain
        this.http
          .get(environment.apiRoot + "/users/self?role_details=true&sub_domain=" + instance.sub_domain + '&environment='+ instance.environment)
          .subscribe(
            (data: ScaffoldUser) => {
              console.log(data);
              if(data.user_id){                
                instances.push(instance.sub_domain);
                resolve(true);
              }else{
                resolve(false);
              }
            },
            (err) => {
              console.log(err);
            }
          );
      });
      promises.push(promise);
    });
    Promise.all(promises).then(() => {
      result.next(instances);
      result.complete();
    });
    return result.asObservable();
  }

  updateRoleInformation(){
    this.http
    .get(environment.apiRoot + "/users/self?role_details=true")
    .subscribe(
      (data: ScaffoldUser) => {
        this._roleDetailsLoaded = true;
        this._roleInformation = data;
        this._roleDetails.next(data);
      },
      (err) => {
        console.log(err);
      }
    );
  }

  checkForMfaEnabled() {
    this.http
      .request("get", environment.apiRoot + "/auth/checkuserpoolmfa")
      .subscribe((response) => {
        if (response === "OPTIONAL") this.dialog.open(MfaModalComponent);
      });
  }

  getAuthenitcation(): Observable<any> {
    //only try to load once;
    if (!this.accessToken) {
      if (!this.accessLoading) {
        this.accessLoading = true;
        this.http
          .post(environment.apiRoot + "/auth/authentication", {
            purpose: "connection",
          })
          .subscribe((data: any) => {
            this.startRefreshTokenTimer();
            this.accessToken = data.accessToken;
            this.sessionDetails = {
              userAttributes: data.cognitoUserAttributes,
            };
            this.authSub.next(this.accessToken);
          });
      }
    } else {
      this.authSub.next(this.accessToken);
    }
    return this.authSub.asObservable();
  }
  
  getUser(id) {
    return this.http.request("get", environment.apiRoot + "/users/" + id + "?output=dataonly&mode=list&key_values=cognito_username");
  }

  getUserByCognito(id, instance, env) {
    return this.http.request("get", environment.apiRoot + "/users/cognito?cognitoId=" + id + "&output=dataonly&mode=list&key_values=cognito_username,role,email,last_name,first_name&sub_domain=" + instance + '&environment=' + env);
  }

  /** Check if in an iframe */
  inIframe(): boolean {
    try {
      console.log(window.self !== window.top);
      return window.self !== window.top;
    } catch (e) {
      console.log("*******************************************", e);
      return true;
    }
  }  

  deleteUserData(id, sub_domain, env) {
    console.log('DELETE',environment.apiRoot + "/users/" + id + "?sub_domain=" + sub_domain + "&environment=" + env)    
    return this.http.delete(environment.apiRoot + "/users/" + id + "/single?sub_domain=" + sub_domain + "&environment=" + env);
  }
}
